import { Grid, Typography } from '@mui/material';
import { IRoom } from '../../../interfaces/IPartnerPms';
import { useEffect, useMemo } from 'react';
import Dialog, { IDefaultDialogProps } from '../../Dialog';
import { ActionButton } from '../../Button';
import { useLanguageContext } from '../../../contexts/LanguageContext';
import DefaultConstants from '../../../data/Constants';
import DateTimeHelper from '../../../helpers/DateTimeHelper';
import { useAuthContext } from '../../../contexts/AuthContext';
import RemoveRoomBlockingConfirmationDialog from './RemoveRoomBlockingConfirmationDialog';
import useDialog from '../../../hooks/useDialog';


interface IRoomBlockingInformationsDialogProps extends IDefaultDialogProps {
    room: IRoom;
    handleRefreshSelectedRoom: () => void;
}


const RoomBlockingInformationsDialog: React.FC<IRoomBlockingInformationsDialogProps> = ({
    open,
    handleClose,
    room,
    handleRefreshSelectedRoom,
}) => {
    const { translate } = useLanguageContext();
    const { hasPermission } = useAuthContext();

    const removeRoomBlockingConfirmationDialog = useDialog();

    useEffect(() => {
        removeRoomBlockingConfirmationDialog.handleClose();
        // eslint-disable-next-line
    }, []);


    const removeRoomBlocking = useMemo(() => {
        return hasPermission(`Governance.RoomOverview.RoomBlocking.Remove`);
        // eslint-disable-next-line
    }, []);


    const dialogActions = useMemo(() => {
        const elements = [
            <ActionButton action='close' onClick={handleClose} />
        ];

        if (removeRoomBlocking) {
            elements.push(<ActionButton action='remove' onClick={() => removeRoomBlockingConfirmationDialog.handleOpen()} />);
        }

        return elements;
        // eslint-disable-next-line
    }, [translate, removeRoomBlocking]);
    

    if (room.roomBlocking === undefined) {
        return null;
    }

    return (
        <>
            <Dialog
                open={open}
                handleClose={handleClose}
                title={translate('blockingInformation')}
                dialogActions={dialogActions}
                className='dialogactions-spacebetween overflow-inherit'
                absolute={true}
                dividers={true}
            >
                <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                    <Grid item md={6} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('room')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.roomDescription ?? '-'}
                        </Typography>
                    </Grid>
            
                    <Grid item md={6} xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('blockingType')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.typeDescription ?? '-'}
                        </Typography>
                    </Grid>
    
                    <Grid item md={6} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('startDate')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.startDate ? DateTimeHelper.Format(room.roomBlocking.startDate, 'DD/MM/YYYY') : '-'}
                        </Typography>
                    </Grid>
    
                    <Grid item md={6} xs={6}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('endDate')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.endDate ? DateTimeHelper.Format(room.roomBlocking.endDate, 'DD/MM/YYYY') : '-'}
                        </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('createdAt')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {
                                room.roomBlocking.createdAt ? (
                                    translate('dateAtTime')
                                        .replace('{date}', DateTimeHelper.Format(room.roomBlocking.createdAt) ?? '-')
                                        .replace('{time}', DateTimeHelper.Format(room.roomBlocking.createdAt, 'HH:mm') ?? '-')
                                ) : '-'
                            }
                        </Typography>
                    </Grid>

                    <Grid item md={6} xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('createdBy')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.createdBy ?? '-'}
                        </Typography>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant='caption' className='fontsize-10 text-color-2'>
                            {translate('description')}
                        </Typography>
            
                        <Typography variant='body2'>
                            {room.roomBlocking.description && room.roomBlocking.description.trim().length > 0 ? room.roomBlocking.description : '-'}
                        </Typography>
                    </Grid>
                </Grid>
            </Dialog>

            {
                removeRoomBlocking && (
                    <RemoveRoomBlockingConfirmationDialog 
                        open={removeRoomBlockingConfirmationDialog.open}
                        handleClose={() => removeRoomBlockingConfirmationDialog.handleClose()}
                        roomBlocking={room.roomBlocking}
                        handleSubmitCallback={() => {
                            handleRefreshSelectedRoom();
                            handleClose();
                        }}
                    />
                )
            }
        </>
    );
}

export default RoomBlockingInformationsDialog;